import { useEffect, useRef } from 'react'
import {
  createChart,
  createSeriesMarkers,
  ColorType,
  LineStyle,
  LineSeries,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type Time,
} from 'lightweight-charts'
import { C, FONT } from '../../theme'
import type { ChartPoint, ChartTrade } from '../../data/liveChart'
import { createBandSeriesView, toBand } from './bandSeries'

const SERIES_CONFIG = [
  { key: 'ourBid', color: C.accent, width: 2 as const, style: LineStyle.Solid },
  { key: 'ourAsk', color: C.accent, width: 2 as const, style: LineStyle.Solid },
  { key: 'binanceBid', color: '#9A9A9A', width: 1 as const, style: LineStyle.Solid },
  { key: 'binanceAsk', color: '#9A9A9A', width: 1 as const, style: LineStyle.Solid },
  { key: 'uniswapBid', color: C.uni, width: 1 as const, style: LineStyle.Dashed },
  { key: 'uniswapAsk', color: C.uni, width: 1 as const, style: LineStyle.Dashed },
] as const

type SeriesKey = (typeof SERIES_CONFIG)[number]['key']

/** Полупрозрачная заливка внутри диапазона — та же пара цветов, что была
 *  в SVG-версии, только теперь через кастомную серию (см. bandSeries.ts),
 *  Binance закрашена первой, чтобы наш диапазон при пересечении лежал сверху. */
const BAND_FILLS = [
  { bid: 'binanceBid', ask: 'binanceAsk', color: C.track } as const,
  { bid: 'ourBid', ask: 'ourAsk', color: C.ourFill } as const,
]

export function LightweightPriceChart({
  height,
  series,
  trades,
}: {
  height: number
  series: Partial<Record<SeriesKey, ChartPoint[]>>
  trades: ChartTrade[]
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRefs = useRef<Partial<Record<SeriesKey, ISeriesApi<'Line'>>>>({})
  const bandRefs = useRef<ISeriesApi<'Custom'>[]>([])
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: C.muted2,
        fontFamily: FONT,
        fontSize: 12,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: C.rule },
      },
      rightPriceScale: { borderColor: C.border },
      timeScale: { borderColor: C.border, timeVisible: true, secondsVisible: false },
      crosshair: { horzLine: { labelBackgroundColor: C.text }, vertLine: { labelBackgroundColor: C.text } },
    })
    chartRef.current = chart

    // Заливки — первыми, чтобы линии рисовались поверх них, а не под ними.
    bandRefs.current = BAND_FILLS.map((cfg) =>
      chart.addCustomSeries(createBandSeriesView(), {
        fillColor: cfg.color,
        priceLineVisible: false,
        lastValueVisible: false,
      }),
    )

    seriesRefs.current = Object.fromEntries(
      SERIES_CONFIG.map((cfg) => [
        cfg.key,
        chart.addSeries(LineSeries, {
          color: cfg.color,
          lineWidth: cfg.width,
          lineStyle: cfg.style,
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerRadius: 3,
        }),
      ]),
    ) as Partial<Record<SeriesKey, ISeriesApi<'Line'>>>

    const bidSeries = seriesRefs.current.ourBid
    if (bidSeries) markersRef.current = createSeriesMarkers(bidSeries, [])

    return () => {
      chart.remove()
      chartRef.current = null
      seriesRefs.current = {}
      bandRefs.current = []
      markersRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    for (const cfg of SERIES_CONFIG) {
      const s = seriesRefs.current[cfg.key]
      const points = series[cfg.key]
      if (s && points) {
        s.setData(points.map((p) => ({ time: p.time as never, value: p.value })))
      }
    }
    BAND_FILLS.forEach((cfg, i) => {
      const band = bandRefs.current[i]
      const bidPts = series[cfg.bid]
      const askPts = series[cfg.ask]
      if (band && bidPts && askPts) {
        band.setData(toBand(bidPts, askPts))
      }
    })
    if (markersRef.current) {
      markersRef.current.setMarkers(
        trades.map((t) => ({
          time: t.time as never,
          position: 'aboveBar',
          color: C.accent,
          shape: 'circle',
          size: 1,
        })),
      )
    }
  }, [series, trades])

  return <div ref={containerRef} style={{ width: '100%', height }} />
}
